/** One uniform error surface for every API call (HTTP, network, timeout, contract drift). */
export type ApiErrorCode =
  | "unauthorized" | "forbidden" | "not_found" | "conflict" | "validation" | "rate_limited"
  | "server" | "unavailable" | "network" | "timeout" | "bad_response" | "aborted" | "unknown";

export class ApiError extends Error {
  override readonly name = "ApiError";
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status = 0,
    readonly details?: unknown,
  ) {
    super(message);
  }
  /** Worth retrying automatically (or offering "Try again"). */
  get retryable(): boolean {
    return ["network", "timeout", "unavailable", "rate_limited", "server"].includes(this.code);
  }
}

export function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 400 || status === 413 || status === 415 || status === 422) return "validation"; // 413/415: e.g. a cover that is too big or not an image — show the server's words
  if (status === 429) return "rate_limited";
  if (status === 502 || status === 503 || status === 504) return "unavailable";
  if (status >= 500) return "server";
  return "unknown";
}

const FRIENDLY: Record<ApiErrorCode, string> = {
  unauthorized: "Your session has ended. Please sign in again.",
  forbidden: "You don't have access to this.",
  not_found: "This item no longer exists.",
  conflict: "This changed in the meantime. Refresh and try again.",
  validation: "Some details need attention.",
  rate_limited: "Too many requests. Try again in a moment.",
  server: "The server hit an error. Try again.",
  unavailable: "ContentOps is temporarily unavailable. Try again shortly.",
  network: "You appear to be offline.",
  timeout: "The request took too long. Try again.",
  bad_response: "The server sent something unexpected.",
  aborted: "Cancelled.",
  unknown: "Something went wrong.",
};

/** A short human sentence for toasts and inline errors; prefers the server's own message for validation/conflict. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if ((err.code === "validation" || err.code === "conflict") && err.message) return err.message;
    return FRIENDLY[err.code];
  }
  return err instanceof Error ? err.message : FRIENDLY.unknown;
}
